import { Button, Flex, Grid, Layout } from "antd";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { AiOutlineFileText, AiOutlineMessage } from "react-icons/ai";
import { useNavigate } from "react-router";

import { Sidebar } from "design-system/components/Sidebar";
import { useMainMenu } from "shell/console/hooks/useMainMenu";
import { featureFlags } from "shell/platform/config/featureFlags";
import { pathsSchema } from "shell/platform/config/paths";
import { useRoles } from "shell/platform/hooks/useRoles";

import s from "./BaseLayout.module.css";
import { Header } from "./Header";
import { NoRoleAssigned } from "./NoRoleAssigned";
import { UserMenu } from "./UserMenu";

const { useBreakpoint } = Grid;
const { Content, Sider } = Layout;

interface BaseLayoutProps {
  children: ReactNode;
  isSidebarVisible?: boolean;
}

export const BaseLayout = ({
  children,
  isSidebarVisible = true,
}: BaseLayoutProps) => {
  const mainMenu = useMainMenu();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const screens = useBreakpoint();

  const isMobile = screens.md !== undefined && !screens.md;

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  const { hasNoRoles, hasInvalidInternalRoleDomain } = useRoles();

  const handleSidebarClick = useCallback(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  const handleOverlayClick = useCallback(() => {
    setCollapsed(true);
  }, []);

  return (
    <Layout className={s.layout}>
      <Header collapsed={collapsed} onCollapse={setCollapsed}>
        <Flex vertical justify="center">
          <Button
            type="text"
            onClick={() => {
              navigate(pathsSchema.featureRequest.index);
            }}
          >
            <AiOutlineMessage /> Request feature
          </Button>
        </Flex>
        <Flex vertical justify="center">
          <Button
            type="text"
            onClick={() => {
              window.open(
                featureFlags.isDev
                  ? pathsSchema.documentation.portal
                  : "https://docs.thredd.com/",
                "_blank",
                "noopener,noreferrer",
              );
            }}
          >
            <AiOutlineFileText /> Documentation Portal
          </Button>
        </Flex>
        {!import.meta.env.VITE_ACCESS_TOKEN_OVERRIDE ? <UserMenu /> : null}
      </Header>
      <Layout>
        {isSidebarVisible && (
          <Sider
            data-testid="sider"
            className={`${s.sider} ${isMobile ? s.mobileSider : ""}`}
            collapsed={collapsed}
            onCollapse={setCollapsed}
            width="256"
            collapsedWidth={isMobile ? 0 : 72}
            theme="light"
            trigger={null}
          >
            <Sidebar
              theme="light"
              inlineCollapsed={collapsed}
              items={mainMenu}
              onClick={handleSidebarClick}
            />
          </Sider>
        )}
        <Layout>
          <Content data-testid="content" className={s.content}>
            <div className={s.inner}>
              {hasNoRoles || hasInvalidInternalRoleDomain ? (
                <NoRoleAssigned invalidDomain={hasInvalidInternalRoleDomain} />
              ) : (
                children
              )}
            </div>
          </Content>
        </Layout>
      </Layout>
      {isMobile && !collapsed && (
        <button
          aria-label="Close sidebar"
          type="button"
          className={s.overlay}
          onClick={handleOverlayClick}
        />
      )}
    </Layout>
  );
};
